import { CallSplit, DoNotDisturb } from '@mui/icons-material';

import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import { ABTestType, CampaignChannels, SplitActions } from 'domains/campaigns/types';
import { Template } from 'domains/content/types';
import useTemplates from 'hooks/queries/useTemplates';

import { CustomCampaignSteps } from '../../CustomCampaign';
import { CampaignSend, CampaignStateTypes } from '../../customCampaignReducer';
import { getEffectiveSplitType } from '../../utils';
import CustomCampaignNavigationButton from '../CustomCampaignNavigationButton';

import { getValueWithNameAndScheduleInfo } from './utils';

type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    index: number;
    send: CampaignSend;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignNavigationSend = ({
    activeSendIndex,
    activeStep,
    campaignState,
    index,
    send,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const isEmailSend = send.channel === CampaignChannels.EMAIL;
    const isDirectMailSend = send.channel === CampaignChannels.DIRECT_MAIL;
    const isSMSSend = send.channel === CampaignChannels.SMS;
    const isVoicecallSend = send.channel === CampaignChannels.VOICE_CALL;
    const { data: templates } = useTemplates({
        enabled: isEmailSend,
    });

    // A/B Testing logic
    const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;
    const isABTesting = isABTestingContent || isABTestingSendTime;
    const isEmailA = isABTesting && index === 0;
    const isEmailB = isABTesting && index === 1;

    const parentSend =
        (send as any).parentIndex != null ? (campaignState.sends[(send as any).parentIndex] as any) : null;
    const parentEffectiveSplitType = parentSend ? getEffectiveSplitType(parentSend, campaignState.sends) : undefined;
    const isBranchRoot = isEmailSend && !!send.splitType && parentEffectiveSplitType !== send.splitType;
    const isSplitSendWithoutAction = isEmailSend && isBranchRoot && !send.splitAction;

    if (isSplitSendWithoutAction)
        return (
            <CustomCampaignNavigationButton
                disabled={!send.splitAction}
                isActive={activeStep === CustomCampaignSteps.SELECT_PATH_ACTION && index === activeSendIndex}
                icon={<CallSplit />}
                label={send.splitType}
                onClick={() => {
                    setActiveSendIndex(index);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                }}
            />
        );

    // These bools and helper funcs are used for both direct mail and email sends
    const isActiveSend =
        (activeStep === CustomCampaignSteps.SELECT_CONTENT || activeStep === CustomCampaignSteps.SCHEDULE_CURATION) &&
        index === activeSendIndex;

    // A/B Testing: Show schedule info for both Email-A and Email-B
    const shouldShowScheduleInfo = isABTesting
        ? (index === 0 || index === 1) && !!send?.scheduledTimestamp
        : index === 0 && !!send?.scheduledTimestamp;

    const SendIcon = channelIcons[send.channel];

    if (isSMSSend && send.content?.id) {
        const contentName = send?.content?.name;
        const contentValue = getValueWithNameAndScheduleInfo(contentName, send, shouldShowScheduleInfo);

        return (
            <CustomCampaignNavigationButton
                isActive={isActiveSend}
                icon={<SendIcon />}
                label="text message"
                onClick={() => {
                    setActiveSendIndex(index);
                    setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
                }}
                value={contentValue}
            />
        );
    }

    if (isDirectMailSend && send.content?.id) {
        const contentValue = getValueWithNameAndScheduleInfo(send.content?.name, send, shouldShowScheduleInfo);

        return (
            <CustomCampaignNavigationButton
                isActive={isActiveSend}
                icon={<SendIcon />}
                label="direct mail"
                labelStyles={{ minWidth: '94px' }}
                onClick={() => {
                    setActiveSendIndex(index);
                    setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
                }}
                value={contentValue}
            />
        );
    }

    // A/B Testing: Show Email A and Email B labels (whether template is selected or not)
    if (isEmailSend && isABTesting && (isEmailA || isEmailB)) {
        const currentTemplate = templates?.find((template: Template) => template.id === send?.templateId);
        const emailValue = getValueWithNameAndScheduleInfo(currentTemplate?.name, send, shouldShowScheduleInfo);
        const abTestLetter = isEmailA ? 'A' : 'B';
        const abTestLabel = isEmailA ? 'Email A' : 'Email B';

        return (
            <>
                <div>{abTestLabel}</div>
                <CustomCampaignNavigationButton
                    isActive={isActiveSend}
                    icon={<SendIcon />}
                    label="email"
                    onClick={() => {
                        setActiveSendIndex(index);
                        setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
                    }}
                    value={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ABTestLabelBox label={abTestLetter} size="small" />
                            {emailValue}
                        </div>
                    }
                />
            </>
        );
    }

    // We want to render a "send" in the nav if there is a send (template has been selected)
    // or if a condtional split will have a send, even before a template has been selected
    if (isEmailSend && (send.templateId || send.splitAction === SplitActions.SEND)) {
        const currentTemplate = templates?.find((template: Template) => template.id === send?.templateId);
        const emailValue = getValueWithNameAndScheduleInfo(currentTemplate?.name, send, shouldShowScheduleInfo);

        return (
            <>
                {isBranchRoot && <div>{send.splitType}</div>}
                <CustomCampaignNavigationButton
                    isActive={isActiveSend}
                    icon={<SendIcon />}
                    label="email"
                    onClick={() => {
                        const targetStep = isBranchRoot
                            ? CustomCampaignSteps.SELECT_PATH_ACTION
                            : CustomCampaignSteps.SELECT_CONTENT;
                        setActiveSendIndex(index);
                        setActiveStep(targetStep);
                    }}
                    value={emailValue}
                />
            </>
        );
    }

    if (isEmailSend && send.splitAction === SplitActions.EXIT)
        return (
            <>
                {isBranchRoot && <div>{send.splitType}</div>}
                <CustomCampaignNavigationButton
                    isActive={activeStep === CustomCampaignSteps.SELECT_PATH_ACTION && index === activeSendIndex}
                    icon={<DoNotDisturb />}
                    label="exit curation"
                    onClick={() => {
                        setActiveSendIndex(index);
                        setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                    }}
                />
            </>
        );

    if (isVoicecallSend && send.content?.name) {
        const name = `${send.content?.name}`;
        const contentValue = getValueWithNameAndScheduleInfo(name, send, shouldShowScheduleInfo);

        return (
            <CustomCampaignNavigationButton
                isActive={isActiveSend}
                icon={<SendIcon />}
                label="voice call"
                onClick={() => {
                    setActiveSendIndex(index);
                    setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
                }}
                value={contentValue}
            />
        );
    }
};

export default CustomCampaignNavigationSend;
